import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Address } from '@ridgewill/brand/react';
import { Modal } from '../components/Modal.js';
import { SecurityGate, type Credential } from '../components/SecurityGate.js';
import { addressesAgree } from '../lib/email-change.js';
import { VerifyFirst } from '../components/EmailVerification.js';
import { useEmailVerified } from '../lib/email-verification.js';
import { Field, Note, Problem, button, input } from '../components/form.js';
import {
  cancelRecoveryChange,
  dropRecoveryAddress,
  enterRecoveryCode,
  isPrimaryAddress,
  requestRecoveryAddress,
  reviewedOn,
  usableFrom,
  type RecoveryState,
} from '../lib/recovery-address.js';
import type { SignInMethods } from '../lib/sign-in-methods.js';

/**
 * A second address that can get you back in when your main mailbox is gone.
 *
 * ── THE CASE THIS EXISTS FOR IS NARROW AND CURRENTLY UNSERVED ────────
 *
 * A password-only account whose holder has forgotten the password AND lost the
 * mailbox. The reset goes to the dead address, moving the address needs that
 * same dead address to approve, and there is no staff route
 * (RidgeWill/auth#332 removed the last one). Permanently out.
 *
 * Somebody who still knows their password is not in that case, and the copy
 * below says so rather than letting them think this is the only way out: they
 * can sign in and add a passkey, which lifts the email-change gate.
 *
 * ── SECOND ON THE PAGE, DIRECTLY UNDER THE ADDRESS IT BACKS UP ───────
 *
 * The two are one subject read in order: here is the address your account lives
 * at, and here is what happens if you lose it. Anywhere further down and it
 * reads as an unrelated setting, which is how a recovery address ends up
 * unset by the people who most need one.
 *
 * ── ASKING SETS NOTHING, AND IT TAKES TWO CONFIRMATIONS ──────────────
 *
 * Harder to say than the email change, which needs one. The new address proves
 * it exists, and the CURRENT address approves — unconditionally, because a
 * recovery address must never be able to create authority it did not already
 * have. Every piece of copy below is written around a 200 meaning "two messages
 * sent", not "done".
 */
export function RecoveryAddress({
  email,
  methods,
  state,
  onChanged,
}: {
  /** The account's primary address, for the same-address check and the copy. */
  email: string;
  methods: SignInMethods | null | undefined;
  /** `undefined` while the lookup is outstanding. */
  state: RecoveryState | undefined;
  /** Re-reads the recovery address. */
  onChanged: () => void;
}) {
  const verified = useEmailVerified();
  if (state === undefined) return <p style={{ margin: 0, color: C.muted }}>Checking&hellip;</p>;

  if (state.status === 'unknown') {
    return (
      /**
       * NOT "you have no recovery address", which is what an empty render would
       * say. This is the one fact on the page somebody would rely on the day
       * they are locked out, and stating it from a failed request is the
       * reassuring direction to be wrong in.
       */
      <Note tone="bad">
        We could not check whether this account has a recovery address. Nothing has changed, so try
        again in a moment.
      </Note>
    );
  }

  return (
    <>
      <Current state={state} onChanged={onChanged} methods={methods} />
      <Pending state={state} email={email} onChanged={onChanged} />
      {/*
        Setting one is refused until the address is proven (auth#587): a
        recovery address planted by whoever registered somebody else's address
        would receive the real owner's reset mail. Reading, cancelling and
        removing stay available.
      */}
      {verified ? (
        <SetForm email={email} methods={methods} state={state} onChanged={onChanged} />
      ) : (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={formHeading}>
            {state.recoveryEmail ? 'Change your recovery address' : 'Add a recovery address'}
          </h3>
          <VerifyFirst />
        </div>
      )}
    </>
  );
}

/** The address in force, if there is one. */
function Current({
  state,
  methods,
  onChanged,
}: {
  state: Extract<RecoveryState, { status: 'loaded' }>;
  methods: SignInMethods | null | undefined;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  if (!state.recoveryEmail) {
    return (
      <Note>
        This account has no recovery address. If you lose access to your email, there is no way back
        in.
      </Note>
    );
  }

  const waiting = usableFrom(state);
  const hasPassword = methods?.hasPassword !== false;

  async function remove(credential: Credential | null) {
    setBusy(true);
    setError(null);
    const out = await dropRecoveryAddress(credential?.password);
    setBusy(false);
    if (typeof out === 'string') {
      setError(out);
      return;
    }
    setOpen(false);
    onChanged();
  }

  return (
    <>
      <Field label="Recovery address">
        <p style={current}>
          <Address>{state.recoveryEmail}</Address>
        </p>
      </Field>

      {waiting ? (
        /**
         * NOT AN ERROR, AND THE TONE MATTERS.
         *
         * A newly established address cannot recover the account for a week.
         * That is the control that turns a silently installed backdoor into a
         * noisy delayed one, so it is working exactly as intended — but on
         * screen it looks like a broken feature unless the copy says when it
         * starts working and why the wait is there.
         */
        <Note>
          This address cannot recover your account until {waiting}. The wait is deliberate: it gives
          you time to notice if somebody else added it.
        </Note>
      ) : null}

      {state.sameDomainAsPrimary ? (
        /**
         * Advisory, never a refusal. A personal Gmail recovering a Google
         * Workspace account is one Google problem away from useless, and most
         * people have not thought about it. The person still decides: an
         * unread mailbox is a worse recovery contact than a correlated one, and
         * the failure this feature is built around is decay, not correlation.
         */
        <Note tone="bad">
          This is at the same provider as your main address, so a problem with that provider could
          take both out at once. An address somewhere else is safer.
        </Note>
      ) : null}

      <Review state={state} />

      {error ? <Problem>{error}</Problem> : null}

      <p style={{ marginTop: '1rem' }}>
        <button type="button" style={button} onClick={() => setOpen(true)} disabled={busy}>
          Remove this recovery address
        </button>
      </p>

      <Modal open={open} title="Remove your recovery address" onClose={() => setOpen(false)}>
        {/*
          Gated like setting one, and for the same reason: somebody who walked
          away from an unlocked laptop should not have their way back in removed
          by a passer-by. There is no approval step, though, because removing
          destroys a route rather than creating one.
        */}
        <SecurityGate
          action="remove your recovery address"
          hasPassword={hasPassword}
          onVerified={(credential) => void remove(credential)}
          onCancel={() => setOpen(false)}
        />
      </Modal>
    </>
  );
}

/**
 * When the address was last confirmed (auth#383).
 *
 * ── THE ASKING MOVED OUT OF HERE, DELIBERATELY ───────────────────────
 *
 * This card used to carry the whole prompt: the explanation, a confirm button,
 * and a pointer to Remove. Jon's 2026-09-05 design moves the question to
 * `RecoveryReviewPrompt`, which renders on every screen in the portal.
 *
 * Leaving a copy here would ask the same question twice on the one screen
 * where both are visible — and the banner's "No, let me change it" navigates
 * to this very card, so the person arriving would be asked again by the thing
 * they were sent to.
 *
 * What stays is the reassuring half, which the banner cannot show because it
 * only appears when a review is DUE: the date of the last confirmation.
 * Nothing at all before the first one — "last confirmed: never" is a reproach
 * for not doing something nobody has been asked for yet, and the address was
 * proved with a code on the day it was set.
 */
function Review({ state }: { state: Extract<RecoveryState, { status: 'loaded' }> }) {
  const on = reviewedOn(state);
  if (!on) return null;
  return (
    <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: C.muted }}>
      You confirmed this address on {on}.
    </p>
  );
}

/**
 * The change waiting on its two confirmations.
 *
 * ── THE TWO ARE INDEPENDENT AND EITHER MAY BE OUTSTANDING ────────────
 *
 * Three renderable states, not two, and they read as different instructions:
 * enter the code from the new address, approve from the current one, or both. A
 * person shown the wrong one waits on a mailbox that is not the one holding
 * them up.
 *
 * The two halves are no longer symmetrical (RidgeWill/auth#393). One is a code
 * typed here; the other is a link clicked in a mailbox. So this card carries a
 * FORM for the first and only a sentence for the second, which is why the code
 * entry lives inside the pending card rather than beside the setup form.
 *
 * Both booleans come from the server. The portal could infer neither.
 */
function Pending({
  state,
  email,
  onChanged,
}: {
  state: Extract<RecoveryState, { status: 'loaded' }>;
  email: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!state.pending) return null;
  const pending = state.pending;

  async function cancel() {
    setBusy(true);
    setError(null);
    const out = await cancelRecoveryChange();
    setBusy(false);
    if (typeof out === 'string') {
      setError(out);
      return;
    }
    onChanged();
  }

  return (
    /**
     * `role="status"`, not `alert`: a standing condition somebody has arrived
     * at rather than something that just happened in front of them. The same
     * choice the pending email change makes, for the same reason.
     */
    <div role="status" style={pendingCard}>
      <h3 style={pendingHeading}>A recovery address is waiting to be confirmed</h3>

      <p style={pendingLine}>
        Somebody asked to make <strong><Address>{pending.recoveryEmail}</Address></strong> the
        recovery address for this account
        {pending.requestedAt ? ` on ${pending.requestedAt.toLocaleString()}` : null}.
      </p>

      <p style={pendingLine}>
        {pending.awaitingVerification && pending.awaitingApproval ? (
          <>
            It needs two things before it takes effect: the six-digit code sent to that address,
            entered below, and approval from your main address,{' '}
            <strong><Address>{email}</Address></strong>. You can do them in either order.
          </>
        ) : pending.awaitingApproval ? (
          <>
            That address is confirmed. It still needs approving from your main address,{' '}
            <strong><Address>{email}</Address></strong>, so check that inbox.
          </>
        ) : (
          <>
            You have approved it from your main address. Enter the six-digit code sent to{' '}
            <strong><Address>{pending.recoveryEmail}</Address></strong> to finish.
          </>
        )}
      </p>

      {pending.awaitingVerification ? <CodeEntry onDone={onChanged} /> : null}

      {state.recoveryEmail ? (
        <p style={pendingLine}>
          Your current recovery address, <strong><Address>{state.recoveryEmail}</Address></strong>,
          keeps working until this one is confirmed.
        </p>
      ) : null}

      {/*
        The sentence this card exists for. Cancelling makes BOTH outstanding
        links inert, which is what stops somebody with a borrowed session
        installing a second way into the account and waiting.
      */}
      <p style={pendingLine}>
        If you did not ask for this, cancel it and then change your password. That signs out every
        other device as well.
      </p>

      {error ? <Problem>{error}</Problem> : null}

      <button type="button" onClick={() => void cancel()} disabled={busy} style={button}>
        {busy ? 'Cancelling…' : 'Cancel this change'}
      </button>
    </div>
  );
}

/**
 * Enter the six-digit code mailed to the proposed address
 * (RidgeWill/auth#393).
 *
 * ── IN THE PENDING CARD, NOT IN A DIALOG ─────────────────────────────
 *
 * The setup form is behind a modal and a security gate because it CREATES a
 * second way into the account. This finishes something the same person started
 * a minute ago, having already passed that gate, so putting it behind another
 * one would be friction with nothing behind it — and worse, it would hide the
 * field from somebody who came back to the page later with the code in hand.
 *
 * It belongs beside the sentence telling them a code was sent, because those
 * two things are one instruction.
 *
 * ── `inputMode="numeric"` IS NOT DECORATION ──────────────────────────
 *
 * This is typed on a phone, from an email open in another app. A numeric keypad
 * is the difference between an easy entry and a fiddly one, and a fiddly one
 * costs an attempt out of five.
 *
 * `autoComplete="one-time-code"` lets the platform offer the code straight from
 * the notification, which is the shortest safe path there is.
 */
function CodeEntry({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Six DIGITS, counted after stripping anything else.
   *
   * The email groups the code as "123 456", so a person copying what they see
   * sends a space. The service normalises it and so does this: a button that
   * stays disabled while the field visibly holds the right code is a dead end
   * somebody cannot reason their way out of.
   */
  const digits = code.replace(/\D/g, '');
  const ready = digits.length === 6;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const out = await enterRecoveryCode(digits);
    setBusy(false);
    if (typeof out === 'string') {
      setError(out);
      return;
    }
    setCode('');
    onDone();
  }

  return (
    <form onSubmit={(e) => void submit(e)} style={{ marginBottom: '.6rem' }}>
      <Field id="recovery-code" label="Six-digit code">
        <input
          id="recovery-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          style={{ ...input, maxWidth: '10rem', fontSize: '1.25rem', letterSpacing: '.2em' }}
        />
      </Field>

      {error ? <Problem>{error}</Problem> : null}

      <button type="submit" disabled={busy || !ready} style={button}>
        {busy ? 'Checking…' : 'Confirm this address'}
      </button>
    </form>
  );
}

/** Ask for a recovery address, or replace the one in force. */
function SetForm({
  email,
  methods,
  state,
  onChanged,
}: {
  email: string;
  methods: SignInMethods | null | undefined;
  state: Extract<RecoveryState, { status: 'loaded' }>;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [credential, setCredential] = useState<Credential | null>(null);
  const [wanted, setWanted] = useState('');
  /** Typed twice, for the reason `addressesAgree` gives: a mistyped address
   *  fails silently, in a mailbox the person does not own. That argument is
   *  stronger here, because nothing about this address is exercised until the
   *  day it is needed. */
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ address: string; delivered: boolean } | null>(null);

  /**
   * Nothing is drawn until the sign-in methods are in, for the reason the
   * Password card gives: a control that changes shape a moment after it appears
   * moves out from under the cursor.
   */
  if (methods === undefined) return <p style={{ margin: 0, color: C.muted }}>Checking&hellip;</p>;

  const hasPassword = methods?.hasPassword !== false;
  const same = isPrimaryAddress(wanted, email);
  const agreed = addressesAgree(wanted, confirm);
  const mismatch = confirm.trim().length > 0 && !agreed;
  const ready = wanted.trim().length > 0 && !same && agreed;
  const replacing = Boolean(state.recoveryEmail) || Boolean(state.pending);

  function close() {
    setOpen(false);
    setCredential(null);
    setWanted('');
    setConfirm('');
    setError(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const out = await requestRecoveryAddress(wanted.trim(), credential?.password);
    setBusy(false);
    if (typeof out === 'string') {
      setError(out);
      return;
    }
    setSent({ address: wanted.trim(), delivered: out.sent });
    close();
    onChanged();
  }

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h3 style={formHeading}>
        {state.recoveryEmail ? 'Change your recovery address' : 'Add a recovery address'}
      </h3>

      <Note>
        Nothing is set when you ask. Two messages go out: a six-digit code to the new address, which
        you enter here, and a link to <strong><Address>{email}</Address></strong> to approve it. It
        takes effect when both are done.
      </Note>

      {sent ? (
        sent.delivered ? (
          <Note tone="ok">
            Your recovery address has not changed yet. Two messages are on their way: a six-digit
            code to <strong><Address>{sent.address}</Address></strong>, which you enter above, and
            an approval link to <strong><Address>{email}</Address></strong>.
          </Note>
        ) : (
          /**
           * The request WORKED and the messages did not go out, which is the
           * normal answer everywhere except production because the service
           * refuses to mail addresses outside its allowlist.
           *
           * Said plainly rather than dressed up as an error: the pending change
           * is real, it is visible in the card above, and it can be cancelled.
           * What the person cannot do is finish it, and leaving them to work
           * that out from two messages that never arrive is the worse failure.
           */
          <Note tone="bad">
            The request was recorded, but the confirmation messages could not be sent from this
            environment. The change is waiting above and you can cancel it, but it cannot be
            completed until they go out.
          </Note>
        )
      ) : null}

      <p style={{ marginTop: '1rem' }}>
        <button type="button" style={button} onClick={() => setOpen(true)}>
          {state.recoveryEmail ? 'Change recovery address' : 'Add a recovery address'}
        </button>
      </p>

      <Modal
        open={open}
        title={state.recoveryEmail ? 'Change your recovery address' : 'Add a recovery address'}
        onClose={close}
      >
        {credential === null ? (
          <SecurityGate
            action="set a recovery address"
            hasPassword={hasPassword}
            onVerified={setCredential}
            onCancel={close}
          />
        ) : (
          <form onSubmit={(e) => void submit(e)}>
            {replacing ? (
              <Note tone="bad">
                Asking again replaces whatever is set or waiting, rather than adding a second
                address.
              </Note>
            ) : null}

            <Field id="recovery-email" label="Recovery email address">
              <input
                id="recovery-email"
                type="email"
                value={wanted}
                onChange={(e) => setWanted(e.target.value)}
                autoComplete="email"
                style={input}
                autoFocus
              />
              {same ? (
                <Note tone="bad">
                  This has to be different from your main address. The point of it is to still work
                  when that one does not.
                </Note>
              ) : null}
            </Field>

            {/*
              `autoComplete="off"` on the second field alone, exactly as the
              email-change form does: a browser offering to fill the address it
              just filled above would defeat the point of asking twice. Paste is
              not blocked, for the reasons recorded there.
            */}
            <Field id="confirm-recovery-email" label="Confirm the recovery address">
              <input
                id="confirm-recovery-email"
                type="email"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="off"
                style={input}
              />
              {mismatch ? <Note tone="bad">These addresses do not match.</Note> : null}
            </Field>

            <Note>
              Pick a mailbox you read and will keep, at a different provider from your main address
              if you can. Anyone who can read it could use it to get into this account.
            </Note>

            {error ? <Problem>{error}</Problem> : null}

            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
              <button type="submit" disabled={busy || !ready} style={button}>
                {busy ? 'Sending…' : 'Send the confirmations'}
              </button>
              <button type="button" onClick={close} style={button}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

/** Looks like the value it is, matching the current-address row above it. */
const current: React.CSSProperties = {
  margin: 0,
  padding: '.6rem 0',
  fontSize: '1rem',
};

/**
 * The same amber card the pending email change uses, deliberately.
 *
 * Two pending states on one screen that looked different would read as two
 * unrelated mechanisms, when they are the same one: something was asked for,
 * nothing has happened yet, and it can be taken back.
 */
const pendingCard: React.CSSProperties = {
  marginTop: '1rem',
  padding: '1rem',
  /* The brand's warn trio, matching `EmailAddress`'s pending card — the two
     are deliberately the same treatment for the same idea (account#87). */
  border: `1px solid ${C.warnDot}`,
  borderRadius: 8,
  background: C.warnBg,
};

const pendingHeading: React.CSSProperties = {
  margin: '0 0 .5rem',
  fontSize: '1rem',
  color: C.warnText,
};

const pendingLine: React.CSSProperties = {
  margin: '0 0 .6rem',
  lineHeight: 1.5,
  fontSize: '.95rem',
};

const formHeading: React.CSSProperties = {
  margin: '0 0 .5rem',
  fontSize: '1rem',
};
